import { SortHeader } from 'components/EntityBrowser/SortHeader';
import { WithPopover } from '@ardoq/popovers';
import { SurveyErrorOrWarningPopover } from './SurveyErrorOrWarningPopover';
import { Folder } from 'components/EntityBrowser/types';
import { ColumnsProps } from 'components/AssetsBrowser/types';
import { ErrorIcon, IconName, WarningIcon } from '@ardoq/icons';
import { colors, s8 } from '@ardoq/design-tokens';
import { AssetRow, SurveyRow } from 'components/AssetsBrowser/types';
import { StatusType, Tag, TagButton } from '@ardoq/status-ui';
import { TagCell, TagCellProps } from '@ardoq/table';
import { CellPlaceholder, HeaderCell, RowType } from '@ardoq/table-legacy';
import { IconDecorator } from '@ardoq/decorators';
import { APISurveyPendingApprovalsSummary, ArdoqId } from '@ardoq/api-types';
import { SurveyValidation } from 'surveyAdmin/types';
import { validationHelpers } from 'surveyAdmin/surveyValidation';
import { dispatchAction } from '@ardoq/rxbeach';
import { navigate } from 'router/declarative/streamRouterAdapter/actions';
import { trackOpenChangeApprovalPage } from 'surveyAdmin/tracking';
import { FlexBox } from '@ardoq/layout';
import { t, type I18nLocale } from '@ardoq/i18n';

const getIcon = (hasErrors: boolean, hasWarnings: boolean) => {
  if (hasErrors) {
    return <ErrorIcon style={{ margin: s8 }} color={colors.iconError} />;
  }
  if (hasWarnings) {
    return <WarningIcon style={{ margin: s8 }} color={colors.iconWarning} />;
  }
};

export const getValidationColumn = (
  surveyValidationObject: Record<string, SurveyValidation>,
  i18nLocale: I18nLocale
) => {
  return {
    valueRender: (_any: any, row: AssetRow | Folder<AssetRow>) => {
      if (row.rowType !== RowType.SURVEY) return <></>;
      const surveyRow = row as SurveyRow;
      const validation = surveyValidationObject[surveyRow._id];
      const hasErrors = validationHelpers.hasError(validation);
      const hasWarnings = validationHelpers.hasWarning(validation);
      const hasErrorsOrWarnings = hasErrors || hasWarnings;
      const icon = getIcon(hasErrors, hasWarnings);
      return (
        validation &&
        hasErrorsOrWarnings && (
          <WithPopover
            content={() => (
              <SurveyErrorOrWarningPopover
                errorMessages={validationHelpers.getOverviewErrorMessages(
                  validation,
                  i18nLocale
                )}
                warningMessages={validationHelpers.getOverviewWarningMessages(
                  validation,
                  i18nLocale
                )}
                i18nLocale={i18nLocale}
              />
            )}
          >
            <FlexBox
              align="center"
              role="alert"
              aria-label={
                hasErrors
                  ? `${t(i18nLocale, 'surveyAdmin:overview.errorsFound', 'Errors:')} ${validationHelpers.getOverviewErrorMessages(validation, i18nLocale).join(', ')}`
                  : `${t(i18nLocale, 'surveyAdmin:overview.warningsFound', 'Warnings:')} ${validationHelpers.getOverviewWarningMessages(validation, i18nLocale).join(', ')}`
              }
            >
              {icon}
            </FlexBox>
          </WithPopover>
        )
      );
    },
  };
};

const getLiveTagProps = (i18nLocale: I18nLocale): TagCellProps => ({
  label: t(i18nLocale, 'surveyAdmin:columns.live', 'Live'),
  leftIcon: { name: IconName.CHECK_MARK },
  statusType: StatusType.SUCCESS,
});

const getDraftTagProps = (i18nLocale: I18nLocale): TagCellProps => ({
  label: t(i18nLocale, 'surveyAdmin:columns.draft', 'Draft'),
  leftIcon: { name: IconName.EDIT },
  statusType: StatusType.INFO,
});

export const getSurveyStatusRender = (
  isPublished: boolean,
  i18nLocale: I18nLocale
) => (
  <TagCell
    {...(isPublished
      ? getLiveTagProps(i18nLocale)
      : getDraftTagProps(i18nLocale))}
  />
);

export const getStatusColumn = (
  columnsProps: ColumnsProps,
  i18nLocale: I18nLocale
) => {
  return {
    title: t(i18nLocale, 'surveyAdmin:columns.status', 'Status'),
    dataIndex: 'published',
    headerStyle: { minWidth: 50 },
    headerRender: ({ title, dataIndex = 'published' }: HeaderCell) => (
      <SortHeader
        dataIndex={dataIndex}
        title={title}
        dataClickId="published"
        sortByField={columnsProps.sortByField}
        sortOrder={columnsProps.sortOrder}
        onSortChanged={columnsProps.setSortByField}
      />
    ),
    valueRender: (_any: any, row: AssetRow | Folder<AssetRow>) => {
      if (row.rowType !== RowType.SURVEY) return <></>;
      const survey = row;
      return getSurveyStatusRender(survey.published, i18nLocale);
    },
  };
};

export const getContactEmailRender = (
  contactEmail: string | undefined,
  i18nLocale: I18nLocale
) => {
  if (!contactEmail)
    return (
      <CellPlaceholder
        placeholder={t(
          i18nLocale,
          'surveyAdmin:columns.noContactEmail',
          'No contact email'
        )}
      />
    );
  return <IconDecorator iconName={IconName.MAIL}>{contactEmail}</IconDecorator>;
};

export const getContactEmailColumn = (
  columnsProps: ColumnsProps,
  i18nLocale: I18nLocale
) => {
  return {
    title: t(i18nLocale, 'surveyAdmin:columns.contactEmail', 'Contact email'),
    dataIndex: 'contactEmail',
    headerStyle: { minWidth: 300 },
    headerRender: ({ title, dataIndex = 'contactEmail' }: HeaderCell) => (
      <SortHeader
        dataIndex={dataIndex}
        title={title}
        dataClickId="contactEmail"
        sortByField={columnsProps.sortByField}
        sortOrder={columnsProps.sortOrder}
        onSortChanged={columnsProps.setSortByField}
      />
    ),
    valueRender: (_any: any, row: AssetRow | Folder<AssetRow>) => {
      if (row.rowType !== RowType.SURVEY) return <></>;
      const survey = row;
      return getContactEmailRender(survey.contactEmail, i18nLocale);
    },
  };
};

const ApprovalTag = ({
  approvalFlowEnabled,
  hasPendingChanges,
  isAdmin,
  openSurveyApprovalPage,
  i18nLocale = {},
}: {
  approvalFlowEnabled: boolean;
  hasPendingChanges: boolean;
  isAdmin: boolean;
  openSurveyApprovalPage: () => void;
  i18nLocale?: I18nLocale;
}) => {
  if (!approvalFlowEnabled)
    return (
      <Tag
        leftIcon={{ name: IconName.CLOSE }}
        label={t(i18nLocale, 'surveyAdmin:columns.approvalOff', 'Approval off')}
      />
    );
  if (hasPendingChanges && isAdmin) {
    return (
      <TagButton
        leftIcon={{ name: IconName.CHECK_MARK }}
        label={t(
          i18nLocale,
          'surveyAdmin:columns.pendingChanges',
          'Pending changes'
        )}
        statusType={StatusType.NEW}
        onClick={openSurveyApprovalPage}
      />
    );
  }
  if (hasPendingChanges) {
    return (
      <Tag
        leftIcon={{ name: IconName.CHECK_MARK }}
        label={t(
          i18nLocale,
          'surveyAdmin:columns.pendingChanges',
          'Pending changes'
        )}
        statusType={StatusType.NEW}
      />
    );
  }
  return (
    <Tag
      leftIcon={{ name: IconName.NOT_INTERESTED }}
      label={t(
        i18nLocale,
        'surveyAdmin:columns.noPendingChanges',
        'No pending changes'
      )}
      statusType={StatusType.INFO}
    />
  );
};

export const getApprovalColumn = (
  surveysWithPendingApprovals: APISurveyPendingApprovalsSummary,
  isOrgAdmin: boolean,
  i18nLocale: I18nLocale
) => {
  const openSurveyApprovalPage = (surveyId: ArdoqId) => {
    trackOpenChangeApprovalPage('Pending changes tag');
    dispatchAction(
      navigate({
        route: '/survey-admin/response-approval/:surveyId',
        params: { surveyId },
      })
    );
  };
  return {
    title: t(i18nLocale, 'surveyAdmin:columns.approval', 'Approval'),
    dataIndex: 'approvalFlow',
    headerStyle: { minWidth: 175 },
    valueRender: (_any: any, row: AssetRow | Folder<AssetRow>) => {
      if (row.rowType !== RowType.SURVEY) return <></>;
      const survey = row;
      const hasPendingChanges = Boolean(
        surveysWithPendingApprovals[survey._id]?.total
      );
      return (
        <FlexBox align="center">
          <ApprovalTag
            approvalFlowEnabled={Boolean(survey.approvalFlow?.enabled)}
            hasPendingChanges={hasPendingChanges}
            isAdmin={isOrgAdmin}
            openSurveyApprovalPage={() => openSurveyApprovalPage(survey._id)}
            i18nLocale={i18nLocale}
          />
        </FlexBox>
      );
    },
  };
};
